import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import cx from 'classnames'

import { useAppSelector } from 'common/hooks/redux'
import { selectRightPanelVisibility } from 'features/unifiedFlow/store/unifiedFlowSlice'

export type RightPaneVisibility = 'none' | 'collapsed' | 'expanded'
const MINIMIZED_WIDTH = 72
const ANIMATION_DURATION = 300

type Props = {
  children: React.ReactNode
  collapsedIcons?: React.ReactNode
  className?: string
  width?: number
  'data-testid'?: string
  onOutsideClick?: () => void
}

const RightPanel = ({
  children,
  className,
  collapsedIcons,
  width = 380,
  'data-testid': dataTestId,
  onOutsideClick,
}: Props) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const heightRef = useRef<number>(0)
  const [isPanelFullyExpanded, setIsPanelFullyExpanded] = useState(false)

  const visibility = useAppSelector(selectRightPanelVisibility)

  useLayoutEffect(() => {
    if (panelRef.current) {
      heightRef.current = panelRef.current.offsetHeight
      const previousSibling = panelRef.current
        .previousElementSibling as HTMLElement
      if (previousSibling) {
        heightRef.current = previousSibling.offsetHeight
      }
    }
  }, [])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (visibility === 'expanded') {
      setIsPanelFullyExpanded(true)
    } else {
      timeoutId = setTimeout(() => {
        setIsPanelFullyExpanded(false)
      }, ANIMATION_DURATION)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [visibility])

  if (visibility === 'none') {
    return null
  }

  const translateX = visibility === 'collapsed' ? width - MINIMIZED_WIDTH : 0

  return (
    <>
      {visibility === 'expanded' && (
        <div
          className={cx(
            'fixed lg:hidden left-0 w-full h-full bg-gold-700 opacity-40 cursor-default z-70'
          )}
          onClick={() => onOutsideClick?.()}
        />
      )}
      <div
        className={cx('relative lg:hidden h-full', {
          flex: isPanelFullyExpanded,
          hidden: !isPanelFullyExpanded,
        })}
        style={{
          width: `${MINIMIZED_WIDTH}px`,
        }}
      ></div>
      <div
        ref={panelRef}
        className={cx(
          `flex flex-col lg:relative min-h-full bg-gold-100 overflow-y-auto
          translate-x-0 transition-all duration-300 ease-in-out z-100`,
          {
            'absolute right-0':
              visibility === 'expanded' || isPanelFullyExpanded,
          },
          className
        )}
        style={{
          width: `${width}px`,
          transform: `translateX(${translateX}px)`,
          marginLeft: `${-translateX}px`,
        }}
        data-testid={dataTestId}
      >
        <div
          className={cx(
            'absolute top-0 left-0 flex flex-col mt-6 mx-6 w-9 gap-4 delay-75 z-10',
            {
              'opacity-0': visibility !== 'collapsed',
              'opacity-100': visibility === 'collapsed',
            }
          )}
        >
          {collapsedIcons}
        </div>

        <div
          className={cx(
            'overflow-auto h-full transition-opacity duration-300 ease-in-out',
            {
              'opacity-0': visibility !== 'expanded',
              'opacity-100': visibility === 'expanded',
            }
          )}
        >
          {children}
        </div>
      </div>
    </>
  )
}

export default RightPanel
